import type { MessengerParentSize } from '@widgets/messenger/types'
import { messengerMobileWidgetShadowOffset, messengerWidgetWidth } from '@widgets/messenger/const'
import { isInIframe } from '@widgets/shared/utils/isInIframe'

const MESSENGER_SEND_PARENT_RESIZE_MESSAGE_TYPE = 'textmagicSendParentResize' as const
const MESSENGER_GET_PARENT_RESIZE_MESSAGE_TYPE = 'textmagicGetParentResize' as const

interface MessengerGetParentResizeMessage {
  type: typeof MESSENGER_GET_PARENT_RESIZE_MESSAGE_TYPE
}

interface MessengerSendParentResizeMessage {
  type: typeof MESSENGER_SEND_PARENT_RESIZE_MESSAGE_TYPE
  payload: MessengerParentSize
}

const isMessengerSendParentResizeMessage = (data: any): data is MessengerSendParentResizeMessage => {
  return data?.type === MESSENGER_SEND_PARENT_RESIZE_MESSAGE_TYPE
}

const isMessengerGetParentResizeMessage = (data: any): data is MessengerGetParentResizeMessage => {
  return data?.type === MESSENGER_GET_PARENT_RESIZE_MESSAGE_TYPE
}

const maxMobileWidth = 768
const maxMessengerWidgetWidth = messengerWidgetWidth + messengerMobileWidgetShadowOffset * 2
export const checkIsMobileParentWindow = (bodyHtml: HTMLElement) => bodyHtml.offsetWidth <= maxMobileWidth
export const checkIsParentSmaller = (bodyHtml: HTMLElement) => bodyHtml.offsetWidth <= maxMessengerWidgetWidth

export const getParentWindowSize = (bodyHtml: HTMLElement): MessengerParentSize => {
  return {
    isMobile: checkIsMobileParentWindow(bodyHtml),
    isParentSmaller: checkIsParentSmaller(bodyHtml),
  }
}

export const sendMessengerParentPositionMessage = (iframe: HTMLIFrameElement, position: MessengerParentSize) => {
  const message = {
    type: MESSENGER_SEND_PARENT_RESIZE_MESSAGE_TYPE,
    payload: position,
  } satisfies MessengerSendParentResizeMessage

  iframe?.contentWindow?.postMessage(message, '*')
}

export const subscribeOnMessengerParentResizeMessages = (callback: (payload: MessengerParentSize) => void) => {
  const listener = ({ data }: MessageEvent) => {
    if (isMessengerSendParentResizeMessage(data)) {
      callback(data.payload)
    }
  }
  window.addEventListener('message', listener)
  return listener
}

export const subscribeOnParentResize = (bodyHtml: HTMLElement, callback: (payload: MessengerParentSize) => void) => {
  const resizeObserver = new ResizeObserver(() => {
    callback(getParentWindowSize(bodyHtml))
  })
  resizeObserver.observe(bodyHtml)
  return () => resizeObserver.disconnect()
}

export const subscribeAndHandleMessengerGetParentResizeMessages = (bodyHtml: HTMLElement) => {
  window.addEventListener('message', (event: MessageEvent) => {
    if (isMessengerGetParentResizeMessage(event.data)) {
      event.source?.postMessage(
        {
          type: MESSENGER_SEND_PARENT_RESIZE_MESSAGE_TYPE,
          payload: getParentWindowSize(bodyHtml),
        },
        {
          targetOrigin: event.origin,
        },
      )
    }
  })
}

export const getParentSize = async (): Promise<MessengerParentSize | undefined> => {
  if (!isInIframe()) {
    return undefined
  }

  const message = {
    type: MESSENGER_GET_PARENT_RESIZE_MESSAGE_TYPE,
  } satisfies MessengerGetParentResizeMessage

  window.parent.postMessage(message, '*')

  return new Promise<MessengerParentSize>((resolve) => {
    const listener = ({ data }: MessageEvent) => {
      if (isMessengerSendParentResizeMessage(data)) {
        resolve(data.payload)
        window.removeEventListener('message', listener)
      }
    }
    window.addEventListener('message', listener)
  })
}
